import React, { useEffect, useState, useMemo } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import { getSecureItem } from "../../services/SecureStorage";
import {
  GetEmployeeDataLogPdfLate,
  getrejectedLates,
  rejectLateIndividual,
} from "../../services/dataService";

function Latelog() {
  const UID = getSecureItem("userId");

  // Server‑side filters (applied when "Show" is clicked)
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [EmployeeName, setEmployeeName] = useState(null);
  const [Code, setCode] = useState(null);

  // Input values (live, for client‑side filtering)
  const [inputFromDate, setInputFromDate] = useState(null);
  const [inputToDate, setInputToDate] = useState(null);
  const [inputEmployeeName, setInputEmployeeName] = useState(null);
  const [inputCode, setInputCode] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [loading, setLoading] = useState(false);
  const [lateData, setLateData] = useState([]);

  const [totalPages, setTotalPages] = useState(0);
  const [isNextPage, setIsNextPage] = useState(false);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedRowSingle, setSelectedRowSingle] = useState(null);

  // Fetch data from server (using the applied filters)
  const getlateData = async (appliedFilters = {}) => {
    try {
      setLoading(true);

      const data = {
        UserId: UID,
        page,
        pageSize,
        fromDate: appliedFilters.hasOwnProperty("fromDate")
          ? appliedFilters.fromDate
          : fromDate,
        toDate: appliedFilters.hasOwnProperty("toDate")
          ? appliedFilters.toDate
          : toDate,
        EmployeeName: appliedFilters.hasOwnProperty("EmployeeName")
          ? appliedFilters.EmployeeName
          : EmployeeName,
        Code: appliedFilters.hasOwnProperty("Code")
          ? appliedFilters.Code
          : Code,
      };

      const response = await getrejectedLates(data);
      const result = response?.data?.data || [];

      setLateData(result);

      if (result.length > 0) {
        setTotalPages(result[0].TotalPages);
        setIsNextPage(result[0].IsNextPage);
      } else {
        setTotalPages(0);
        setIsNextPage(false);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // Called when "Show" is clicked – applies filters and fetches fresh data
  const handleSearchApply = () => {
    setFromDate(inputFromDate);
    setToDate(inputToDate);
    setEmployeeName(inputEmployeeName);
    setCode(inputCode);
    setPage(1);

    getlateData({
      fromDate: inputFromDate,
      toDate: inputToDate,
      EmployeeName: inputEmployeeName,
      Code: inputCode,
    });
  };

  // Download PDF report
  const handleDownloadPdf = async () => {
    try {
      const response = await GetEmployeeDataLogPdfLate({
        UserId: UID,
        page,
        pageSize: 0,
        fromDate,
        toDate,
        EmployeeName,
        Code,
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "employee-log.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  };

  // Open modal for reverting a log entry
  const openRevertModal = (item) => {
    setSelectedRowSingle(item);
    setReason("");
    setOpenModal(true);
  };

  // Submit revert
  const handleRevert = async () => {
    try {
      if (!reason) {
        alert("Please enter revert reason");
        return;
      }
      const data = {
        EmpId: selectedRowSingle.EmpId,
        Recorddate: selectedRowSingle.Recorddate,
        UserId: UID,
        RejectReason: reason,
        RejectLateStatus: 0,
        RecordType: selectedRowSingle.RecordType,
      };
      await rejectLateIndividual(data);
      setOpenModal(false);
      getlateData(); // refresh list
    } catch (err) {
      console.log(err);
    }
  };

  // Re‑fetch when page changes
  useEffect(() => {
    getlateData();
  }, [page]);

  // ─── CLIENT‑SIDE FILTERING (live search) ────────────────────────────────
  const filteredData = lateData.filter((item) => {
    if (inputEmployeeName && item.EmployeeName) {
      if (!item.EmployeeName.toLowerCase().includes(inputEmployeeName.toLowerCase())) {
        return false;
      }
    }
    if (inputCode && item.EmployeeCode) {
      if (!item.EmployeeCode.toLowerCase().includes(inputCode.toLowerCase())) {
        return false;
      }
    }
    if (inputFromDate && item.Recorddate) {
      const itemDate = new Date(item.Recorddate).setHours(0, 0, 0, 0);
      const filterFrom = new Date(inputFromDate).setHours(0, 0, 0, 0);
      if (itemDate < filterFrom) return false;
    }
    if (inputToDate && item.Recorddate) {
      const itemDate = new Date(item.Recorddate).setHours(0, 0, 0, 0);
      const filterTo = new Date(inputToDate).setHours(0, 0, 0, 0);
      if (itemDate > filterTo) return false;
    }
    return true;
  });

  // ─── LATEST ENTRY PER EMPLOYEE + DATE ───────────────────────────────────
  // Only the most recent log entry (by SysTime) for each (EmpId, Recorddate)
  // should have the Revert button enabled.
  const latestIds = useMemo(() => {
    const groups = {};
    filteredData.forEach((item) => {
      const key = `${item.EmpId}|${item.Recorddate}`;
      if (!groups[key] || new Date(item.SysTime) > new Date(groups[key].SysTime)) {
        groups[key] = item;
      }
    });
    return new Set(Object.values(groups).map((item) => item.ID));
  }, [filteredData]);

  // ─── RENDER ──────────────────────────────────────────────────────────────

  return (
    <div className="p-4">
      <Breadcrumb title={"Late Log"} />

      {/* Filters */}
      <div className="bg-white shadow-md rounded-xl p-4 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">From Date</label>
            <input
              type="date"
              value={inputFromDate || ""}
              onChange={(e) => setInputFromDate(e.target.value || null)}
              className="w-full border rounded-lg px-3 py-2 mt-1 outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium">To Date</label>
            <input
              type="date"
              value={inputToDate || ""}
              onChange={(e) => setInputToDate(e.target.value || null)}
              className="w-full border rounded-lg px-3 py-2 mt-1 outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Employee Name</label>
            <input
              type="text"
              placeholder="Enter employee name"
              value={inputEmployeeName || ""}
              onChange={(e) => setInputEmployeeName(e.target.value || null)}
              className="w-full border rounded-lg px-3 py-2 mt-1 outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Employee Code</label>
            <input
              type="text"
              placeholder="Enter employee code"
              value={inputCode || ""}
              onChange={(e) => setInputCode(e.target.value || null)}
              className="w-full border rounded-lg px-3 py-2 mt-1 outline-none"
            />
          </div>
        </div>

        <div className="mt-4 gap-2">
          <button
            onClick={handleSearchApply}
            className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-medium"
          >
            {loading ? "Loading..." : "Show"}
          </button>
          <button
            onClick={handleDownloadPdf}
            className="bg-green-600 ms-3 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-xl p-4 mt-5 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-sm text-gray-700 border-b">
              <th className="p-3 text-left">SL</th>
              <th className="p-3 text-left">Employee</th>
              <th className="p-3 text-left">Record Date</th>
              <th className="p-3 text-left">Reason</th>
              <th className="p-3 text-left">Rejected By</th>
              <th className="p-3 text-left">Update On</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => {
                const isLatest = latestIds.has(item.ID);
                const isRevertDisabled = !isLatest || item.RejectLateStatus === 0;

                return (
                  <tr
                    key={item.ID}
                    className="border-b hover:bg-gray-50 text-sm text-gray-800"
                  >
                    <td className="p-3 font-medium">{index + 1}</td>
                    <td className="p-3 font-medium">{item.EmployeeName}</td>
                    <td className="p-3">
                      {item.Recorddate
                        ? new Date(item.Recorddate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="p-3">
                      {item.RejectReason === "null" || !item.RejectReason
                        ? "-"
                        : item.RejectReason}
                    </td>
                    <td className="p-3">{item.UserName || "-"}</td>
                    <td className="p-3">
                      {item.SysTime
                        ? new Date(item.SysTime).toLocaleString()
                        : "-"}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          item.RejectLateStatus === 2
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item.RejectLateStatus === 2
                          ? item.RecordType === "Late"
                            ? "Late rejected"
                            : "Early rejected"
                          : item.RecordType === "Late"
                          ? "Late reverted"
                          : "Early reverted"}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        disabled={isRevertDisabled}
                        onClick={() => openRevertModal(item)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-medium text-white transition-colors ${
                          isRevertDisabled
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-red-500 hover:bg-red-600"
                        }`}
                      >
                        Revert
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center p-8 text-gray-400 font-medium">
                  No Records Found Matching Filter Criteria
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-5">
          <div className="text-sm text-gray-500">
            Page <span className="font-semibold text-gray-700">{page}</span> of{" "}
            <span className="font-semibold text-gray-700">{totalPages}</span>
          </div>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                page === 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              Prev
            </button>
            <button
              disabled={!isNextPage}
              onClick={() => setPage(page + 1)}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                !isNextPage
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {openModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-opacity-60 backdrop-blur-sm z-[1000]">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-5 mx-4">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">
              Enter Revert Reason
            </h2>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter revert reason..."
              className="w-full border rounded-lg p-3 outline-none focus:border-orange-500"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setOpenModal(false)}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRevert}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Latelog;